'use client';

import type { FormAnswerValue, FormField } from '@/types';
import {
  formatOnboardingAnswerText,
  formatOnboardingDateAnswer,
  isOnboardingAnswerEmpty,
  isOnboardingImageUrl,
} from '@/lib/onboarding-answers';

interface OnboardingAnswerDisplayProps {
  field?: FormField;
  answer: FormAnswerValue | undefined;
  emptyLabel?: string;
  imageClassName?: string;
}

function renderImage(url: string, alt: string, className: string) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block mt-1 relative group cursor-zoom-in"
    >
      <img src={url} alt={alt} className={className} loading="lazy" />
    </a>
  );
}

export function OnboardingAnswerDisplay({
  field,
  answer,
  emptyLabel = 'Not provided',
  imageClassName = 'max-w-sm max-h-80 w-auto h-auto rounded-xl border border-border object-contain',
}: OnboardingAnswerDisplayProps) {
  if (isOnboardingAnswerEmpty(answer)) {
    return (
      <span className="text-sm text-muted-foreground/90 italic font-medium">{emptyLabel}</span>
    );
  }

  if (Array.isArray(answer)) {
    const imageItems = answer.filter(
      (item): item is string => typeof item === 'string' && isOnboardingImageUrl(item)
    );
    const textItems = answer.filter(
      (item) => !(typeof item === 'string' && isOnboardingImageUrl(item))
    );

    return (
      <div className="space-y-2">
        {textItems.length > 0 && (
          <span className="text-sm font-semibold text-foreground block">
            {textItems.map(String).join(', ')}
          </span>
        )}
        {imageItems.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {imageItems.map((url, index) =>
              renderImage(url, `Uploaded answer ${index + 1}`, imageClassName)
            )}
          </div>
        )}
      </div>
    );
  }

  if (typeof answer === 'string' && isOnboardingImageUrl(answer)) {
    return renderImage(answer, 'Uploaded answer', imageClassName);
  }

  if (typeof answer === 'string') {
    const formatted =
      field?.type === 'DATE' || /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(answer.trim())
        ? formatOnboardingDateAnswer(answer)
        : null;
    if (formatted) {
      return <span className="text-sm font-semibold text-foreground">{formatted}</span>;
    }
  }

  return (
    <span className="text-sm font-semibold text-foreground">
      {formatOnboardingAnswerText(field, answer)}
    </span>
  );
}
